import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Minus, ArrowUpRight, Loader2, Crown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Reveal } from "../components/site/Reveal";

// Feature matrix: which tier keys unlock each perk.
const FEATURES = [
  { en: "Monthly newsletter", fr: "Infolettre mensuelle", tiers: ["explorer", "member", "circle_leader"] },
  { en: "Access to open events", fr: "Accès aux événements ouverts", tiers: ["explorer", "member", "circle_leader"] },
  { en: "Community updates", fr: "Nouvelles de la communauté", tiers: ["explorer", "member", "circle_leader"] },
  { en: "Member event pricing", fr: "Tarifs membres aux événements", tiers: ["member", "circle_leader"] },
  { en: "Full member directory", fr: "Répertoire complet des membres", tiers: ["member", "circle_leader"] },
  { en: "Exclusive workshops", fr: "Ateliers exclusifs", tiers: ["member", "circle_leader"] },
  { en: "Monthly circle calls", fr: "Appels mensuels du cercle", tiers: ["member", "circle_leader"] },
  { en: "Bring a guest", fr: "Amenez une invitée", tiers: ["member", "circle_leader"] },
  { en: "Host your own circle", fr: "Animez votre propre cercle", tiers: ["circle_leader"] },
  { en: "VIP gala seating", fr: "Places VIP aux galas", tiers: ["circle_leader"] },
  { en: "Speaker nominations", fr: "Nominations de conférencières", tiers: ["circle_leader"] },
  { en: "1:1 mentorship matching", fr: "Jumelage de mentorat 1:1", tiers: ["circle_leader"] },
];

export default function MembershipPlans() {
  const { lang, t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [loadingKey, setLoadingKey] = useState(null);
  const [cfg, setCfg] = useState({ annual_free_months: 2 });
  const [cycle, setCycle] = useState("monthly");

  useEffect(() => {
    api.get("/membership-tiers").then((r) => setTiers(r.data.sort((a, b) => a.order - b.order))).catch(() => {});
    api.get("/membership-config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const yearlyOf = (p) => p * Math.max(1, 12 - cfg.annual_free_months);

  const priceOf = (key) => tiers.find((x) => x.key === key)?.price ?? 0;
  const currentPrice = user ? priceOf(user.tier) : -1;

  const startCheckout = async (key) => {
    if (!user) { navigate(`/join?tier=${key}`); return; }
    setLoadingKey(key);
    try {
      const { data } = await api.post("/payments/checkout", { tier_key: key, origin_url: window.location.origin, user_id: user.id });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      if (data.free) { navigate("/dashboard"); return; }
      toast.info(data.message || "Please try again.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout");
    } finally {
      setLoadingKey(null);
    }
  };

  const ctaFor = (tr) => {
    const isCurrent = user && user.role !== "admin" && user.tier === tr.key;
    const isUpgrade = user && user.role !== "admin" && (tr.price ?? 0) > currentPrice;
    const isLower = user && user.role !== "admin" && (tr.price ?? 0) < currentPrice;
    if (isCurrent) return <span data-testid={`plan-current-${tr.key}`} className="inline-flex items-center justify-center w-full rounded-full bg-secondary/15 text-secondary px-5 py-2.5 text-sm font-semibold">{t("Your plan", "Votre forfait")}</span>;
    if (isLower) return <span className="inline-flex items-center justify-center w-full rounded-full bg-muted text-muted-foreground px-5 py-2.5 text-sm font-semibold">{t("Included", "Inclus")}</span>;
    const label = !user ? (tr.price === 0 ? t("Join free", "Gratuit") : (lang === "fr" ? tr.cta_fr : tr.cta_en))
      : isUpgrade ? t("Upgrade", "Améliorer") : (lang === "fr" ? tr.cta_fr : tr.cta_en);
    return (
      <button data-testid={`plan-cta-${tr.key}`} onClick={() => startCheckout(tr.key)} disabled={loadingKey === tr.key}
        className="inline-flex items-center justify-center gap-2 w-full rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:bg-secondary transition-colors disabled:opacity-60">
        {loadingKey === tr.key ? <Loader2 size={15} className="animate-spin" /> : <ArrowUpRight size={15} />} {label}
      </button>
    );
  };

  return (
    <div data-testid="membership-plans-page" className="max-w-6xl mx-auto px-5 md:px-8 pb-24">
      <Reveal>
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs uppercase tracking-[0.25em] text-secondary font-semibold">{t("Membership", "Adhésion")}</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-medium text-primary tracking-tight mt-3">{t("Find the tier that fits.", "Trouvez le niveau qui vous convient.")}</h1>
          <p className="text-base md:text-lg text-foreground/70 mt-4">{t("Every level opens the door. The higher you go, the more of the circle you unlock.", "Chaque niveau ouvre la porte. Plus vous montez, plus vous débloquez le cercle.")}</p>
          <div className="inline-flex items-center gap-1 mt-7 p-1 rounded-full bg-muted/60 border border-primary/10" data-testid="plans-billing-toggle">
            {["monthly", "yearly"].map((c) => (
              <button key={c} data-testid={`plans-billing-${c}`} onClick={() => setCycle(c)}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${cycle === c ? "bg-primary text-primary-foreground" : "text-primary/70 hover:text-primary"}`}>
                {c === "monthly" ? t("Monthly", "Mensuel") : t("Yearly", "Annuel")}
                {c === "yearly" && <span className="ml-2 text-[11px] rounded-full bg-secondary/20 text-secondary px-2 py-0.5">{cfg.annual_free_months} {t("mo free", "mois offerts")}</span>}
              </button>
            ))}
          </div>
        </div>
      </Reveal>

      {/* Tier header cards */}
      <div className="grid md:grid-cols-3 gap-6 mt-12">
        {tiers.map((tr, i) => (
          <Reveal key={tr.key} delay={i * 0.08}>
            <div className={`relative rounded-3xl p-7 border-2 h-full flex flex-col ${tr.popular ? "border-secondary bg-card shadow-xl shadow-secondary/10" : "border-primary/10 bg-card"}`}>
              {tr.popular && <span className="absolute -top-3 left-7 inline-flex items-center gap-1 text-xs uppercase tracking-widest font-semibold bg-secondary text-secondary-foreground rounded-full px-3 py-1"><Crown size={12} /> {t("Most popular", "Le plus populaire")}</span>}
              <h3 className="font-display text-2xl font-semibold text-primary">{lang === "fr" ? tr.name_fr : tr.name_en}</h3>
              <p className="mt-1 mb-5"><span className="font-display text-4xl font-semibold text-primary">{tr.price === 0 ? t("Free", "Gratuit") : `$${cycle === "yearly" ? yearlyOf(tr.price) : tr.price}`}</span>{tr.price > 0 && <span className="text-muted-foreground">/{cycle === "yearly" ? t("yr", "an") : t("mo", "mois")}</span>}</p>
              <div className="mt-auto">{ctaFor(tr)}</div>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Comparison matrix */}
      <Reveal>
        <div className="mt-14 bg-card border border-primary/10 rounded-3xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm" data-testid="plans-comparison-table">
              <thead>
                <tr className="border-b border-primary/10">
                  <th className="text-left px-6 py-5 font-display text-lg text-primary font-semibold">{t("What's included", "Ce qui est inclus")}</th>
                  {tiers.map((tr) => (
                    <th key={tr.key} className="px-4 py-5 text-center font-semibold text-primary">{lang === "fr" ? tr.name_fr : tr.name_en}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {FEATURES.map((f, idx) => (
                  <tr key={idx} className="border-b border-primary/5 last:border-0">
                    <td className="px-6 py-4 text-foreground/85">{lang === "fr" ? f.fr : f.en}</td>
                    {tiers.map((tr) => (
                      <td key={tr.key} className="px-4 py-4 text-center">
                        {f.tiers.includes(tr.key)
                          ? <Check size={18} className="inline text-secondary" />
                          : <Minus size={16} className="inline text-muted-foreground/40" />}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td className="px-6 py-5" />
                  {tiers.map((tr) => (
                    <td key={tr.key} className="px-4 py-5 text-center">{ctaFor(tr)}</td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </Reveal>

      <p className="text-center text-sm text-muted-foreground mt-8">
        {t("Questions about membership?", "Des questions sur l'adhésion ?")} <Link to="/contact" className="text-secondary font-semibold hover:underline">{t("Talk to us", "Parlez-nous")}</Link>
      </p>
    </div>
  );
}
